<script setup>
import { ref } from "vue";
import { useRouter, useRoute } from "vue-router";
import { supabase } from "@/lib/supabase";

/*
  SubscriptionsView.vue
  - Shows three editable plan cards: Monthly, Yearly, 100 Yearly
  - Each card uses a priceId (Stripe Price) passed to a single subscribe handler
  - If user is not logged in, redirect to /login with intended plan in query
  - Edit the `plans` array below to change copy, price labels, features, or priceIds
*/

const router = useRouter();
const route = useRoute();

// Easily editable plan definitions
const plans = ref([
  {
    key: "monthly",
    name: "Monthly",
    priceDisplay: "$2.99 / month",
    // priceId: "prod_price_monthly_replace_me",
    priceId: "price_1S1aDNQHyLSDk8AHYQeCeBkN", // Update all of the price ids back to prod
    badge: "Most flexible",
    description: "Great for getting started.",
    features: [
      "Access to all features and languages",
      "Unlimited cards",
      "Email Support",
      "Cancel anytime",
    ],
  },
  {
    key: "yearly",
    name: "Yearly",
    priceDisplay: "$27.99 / year",
    // priceId: "prod_price_yearly_replace_me",
    priceId: "price_1S1aEmQHyLSDk8AHs4G0NKxC",
    badge: "Save 20%",
    description: "Best value billed annually.",
    features: [
      "Everything in Monthly",
      "10% discount over monthly",
      "Phone support",
      "Early access to beta features",
    ],
    popular: true,
  },
  {
    key: "lifetime",
    name: "Lifetime",
    priceDisplay: "$49.99 one-time",
    // priceId: "prod_price_lifetime_replace_me",
    priceId: "price_1SB3fiQHyLSDk8AHw8CntC0Q",
    badge: "Lifetime access",
    description: "Pay once, access forever.",
    features: [
      "Everything in Yearly and Monthly",
      "Flat rate for lifetime usage",
      "Perfect for power learners",
      "Supports the development of new features",
    ],
  },
]);

// Optional: preselect a plan when arriving with ?plan= in the URL
const selectedPlanKey = ref(null);
if (typeof route?.query?.plan === "string") {
  const candidate = String(route.query.plan);
  if (plans.value.some((p) => p.key === candidate)) {
    selectedPlanKey.value = candidate;
  }
}
const selectPlan = (key) => {
  selectedPlanKey.value = key;
};

const subscribingKey = ref(null);
const errorMsg = ref("");

const subscribe = async (plan) => {
  errorMsg.value = "";
  if (!plan?.priceId) {
    errorMsg.value = "Price configuration missing for this plan.";
    return;
  }

  // Ensure auth
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user) {
    // Redirect to login then return to subscriptions, preserving intended plan
    router.push({
      path: "/login",
      query: { redirect: "/subscriptions", plan: plan.key },
    });
    return;
  }

  // Invoke your edge function to create a Stripe Checkout Session
  try {
    subscribingKey.value = plan.key;
    const { data, error } = await supabase.functions.invoke(
      "create-checkout-session",
      { body: { priceId: plan.priceId } }
    );
    console.log("create-checkout-session response", { data, error });
    if (error) {
      console.error("create-checkout-session invoke error", error, data);
      const serverMessage =
        error?.message ||
        (data && (data.error || data.message)) ||
        "Unknown error";
      errorMsg.value = `Checkout failed: ${serverMessage}`;
      return;
    }
    if (data?.url) {
      window.location.href = data.url;
    } else {
      console.error("create-checkout-session: missing url in response", {
        data,
      });
      const serverMessage =
        (data && (data.error || data.message)) ||
        "Unable to start checkout. Please try again.";
      errorMsg.value = serverMessage;
    }
  } catch (e) {
    console.error("subscribe() unexpected error", e);
    const msg =
      e?.message || String(e) || "Subscription failed. Please try again.";
    errorMsg.value = `Subscription failed: ${msg}`;
  } finally {
    subscribingKey.value = null;
  }
};
</script>

<template>
  <div class="subscriptions">
    <header class="header">
      <h1>Choose your plan</h1>
      <p class="sub">
        Pick the plan that fits your learning pace. You can change or cancel
        anytime.
      </p>
    </header>

    <div v-if="errorMsg" class="error">{{ errorMsg }}</div>

    <div class="cards">
      <article
        v-for="plan in plans"
        :key="plan.key"
        class="card"
        :class="{
          popular: !!plan.popular,
          selected: selectedPlanKey === plan.key,
        }"
        @click="selectPlan(plan.key)"
      >
        <div class="card-header">
          <h2 class="name">{{ plan.name }}</h2>
          <span v-if="plan.badge" class="badge">{{ plan.badge }}</span>
        </div>

        <div class="price">{{ plan.priceDisplay }}</div>
        <p class="desc">{{ plan.description }}</p>

        <ul class="features">
          <li v-for="f in plan.features" :key="f">{{ f }}</li>
        </ul>

        <button
          class="btn"
          :disabled="subscribingKey === plan.key"
          @click.stop="subscribe(plan)"
        >
          {{
            subscribingKey === plan.key ? "Starting checkout..." : "Subscribe"
          }}
        </button>
      </article>
    </div>

    <div class="footnote">
      By subscribing you agree to our
      <router-link to="/terms">Terms and Privacy Policy</router-link>.
      Subscriptions renew automatically unless cancelled.
    </div>
  </div>
</template>

<style scoped>
.subscriptions {
  padding: 20px;
  margin-left: 75px; /* align with sidebar */
  color: var(--text-color);
}

.header h1 {
  margin: 0 0 6px;
  font-weight: 700;
}
.header .sub {
  margin: 0 0 16px;
  color: color-mix(in oklab, var(--text-color) 80%, transparent);
}

.error {
  background: #fde2e2;
  color: #7f1d1d;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 12px;
  font-size: 0.95rem;
}

.cards {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 8px;
}

.card {
  background: var(--sidebar-bg);
  border-radius: 12px;
  padding: 14px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  border: 1px solid transparent;
  cursor: pointer;
  transition: border-color 0.15s ease, transform 0.06s ease,
    box-shadow 0.15s ease;
}
.card:hover {
  transform: translateY(-1px);
  border-color: color-mix(in oklab, var(--text-color) 10%, transparent);
}
.card.selected {
  border-color: var(--accent);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.card.popular {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.badge {
  background: var(--accent);
  color: #fff;
  border-radius: 999px;
  padding: 4px 8px;
  font-size: 0.8rem;
  font-weight: 600;
}
.name {
  margin: 0;
  font-size: 1.1rem;
}
.price {
  font-size: 1.4rem;
  font-weight: 800;
  margin: 6px 0 8px;
}
.desc {
  margin: 0 0 8px;
  color: color-mix(in oklab, var(--text-color) 78%, transparent);
}

.features {
  list-style: none;
  padding: 0;
  margin: 0 0 12px;
  display: grid;
  gap: 6px;
}
.features li {
  display: flex;
  gap: 8px;
}
.features li::before {
  content: "✓";
  color: var(--accent);
  font-weight: 800;
}

.btn {
  width: 100%;
  padding: 10px 14px;
  background: #10b981; /* emerald-500 */
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.06s ease, background 0.15s ease;
}
.btn:hover {
  transform: translateY(-1px);
  background: #0ea672;
}
.btn:disabled {
  opacity: 0.7;
  cursor: default;
  transform: none;
}

.footnote {
  margin-top: 10px;
  font-size: 0.85rem;
  color: color-mix(in oklab, var(--text-color) 72%, transparent);
}

@media (max-width: 980px) {
  .cards {
    grid-template-columns: 1fr;
  }
  .subscriptions {
    margin-left: 0;
  }
  .header h1 {
    margin-top: 25px;
  }
}
</style>
